import React, { FC, ReactElement, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, withRouter } from 'react-router-dom';
import ErrorMessage from '../../components/common/ErrorMessage';
import ParseHtml from '../../components/common/ParseHtml';
import withLocalization from '../../components/hoc/withLocalization';
import withRoles from '../../components/hoc/withRoles';
import SearchPanel from '../../components/OfferSearch/Search';
import SoldPackageInformationDetails from '../../components/OfferSearch/SoldPackageInformationDetails';
import { isSessionExpired } from '../../redux/middleware';
import { RootState } from '../../redux/rootReducer';
import { LOC, OFFER_DESTINATION_PATH, PIN } from '../../util/strings';
import {
  releaseHoldProperty,
  resetADACodes,
  // eslint-disable-next-line prettier/prettier
  resetAllEligibilityOptions
} from '../offerAvailability/offerAvailabilitySlice';
import { ReleaseHoldPropertyRequest } from '../offerAvailability/types';
import {
  resetBonvoyValue,
  resetConfirmationComments,
  resetConfirmationNumber,
  resetErrorStatus,
  resetIIValue,
  resetPartnerCode,
  resetPartnerId,
  setPaymentButtonState,
  // eslint-disable-next-line prettier/prettier
  setPaymentcheckBox
} from '../offerConfirmation/offerConfirmationSlice';
import { resetAllContactBillingInfo, resetEffortCode, resetFlipOfferDisplayMode } from './offerSearchSlice';
import { Prospect, SoldPackageInformation } from './types';

const OfferSearch: FC<any> = (props: any): ReactElement => {
  const { localizeImages, localize } = props;
  const { path } = localizeImages('preview-sales-engine.static-content.images.search-page-hero', 0, '4:3');
  const dispatch = useDispatch();
  const LazyProspectInfo = React.lazy(() => import('../../components/OfferSearch/ProspectInformation'));
  const LazyLOCInfo = React.lazy(() => import('../../components/OfferSearch/LOCDetails'));

  const state = useSelector((state: RootState) => state.offerDetails);
  const holdSessionId = useSelector((state: RootState) => state.availabilityDates.holdSessionId);
  const locDetails = state.locDescription;
  const prospectDetails: Prospect | undefined = state?.prospect;
  const isSearchLoading: boolean = state?.loading;
  const showProspect: boolean = state?.showProspect;
  const hasErrors: boolean = state?.hasError;
  const errorObj = state?.error;
  const isLocSearch = state?.searchMode;
  const renderProspect = showProspect && !isSearchLoading && isLocSearch === PIN;
  const spinner = isSearchLoading ? <></> : <></>;
  const isPackageSold = state.isPackageSold;
  const soldPackageInformation: SoldPackageInformation | undefined = state.soldPackageInformation;
  const renderLOCInfo = !isPackageSold && !isSearchLoading && isLocSearch === LOC;
  const lastActiveTime = useSelector((state: RootState) => state.sessionManager).lastActiveTime;

  useEffect(() => {
    const resetAppState = sessionStorage.getItem('RESET_APP_STATE');
    if (resetAppState === null) {
      dispatch(resetFlipOfferDisplayMode());
      dispatch(resetConfirmationNumber());
      dispatch(resetErrorStatus());
      dispatch(resetADACodes());
      dispatch(resetAllContactBillingInfo());
      dispatch(setPaymentButtonState(false));
      dispatch(setPaymentcheckBox(false));
      dispatch(resetConfirmationComments());
      dispatch(resetAllEligibilityOptions());
      dispatch(resetEffortCode());
      dispatch(resetPartnerId());
      dispatch(resetPartnerCode());
      dispatch(resetBonvoyValue());
      dispatch(resetIIValue());
    } else {
      sessionStorage.removeItem('RESET_APP_STATE');
    }
  }, [dispatch]);

  useEffect(() => {
    if (holdSessionId) {
      const releaseRequest: ReleaseHoldPropertyRequest = {
        holdId: holdSessionId,
      };
      dispatch(releaseHoldProperty(releaseRequest));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holdSessionId]);

  const onShowOfferClick = () => {
    props.history.push(OFFER_DESTINATION_PATH);
  };
  const prospect = renderProspect ? (
    <Suspense fallback={<></>}>
      <LazyProspectInfo prospect={prospectDetails} onShowOfferClick={onShowOfferClick} />
    </Suspense>
  ) : (
    <></>
  );
  const locDescription = renderLOCInfo ? (
    <Suspense fallback={<></>}>
      <LazyLOCInfo locDescription={locDetails} onShowOfferClick={onShowOfferClick} />
    </Suspense>
  ) : (
    <></>
  );
  const error = hasErrors ? <ErrorMessage errorObject={errorObj}></ErrorMessage> : <></>;
  const renderSearchPanel = !renderProspect && !renderLOCInfo;
  const styleBg = {
    backgroundImage: `url("${path}")`,
  };
  return (
    <>
      <div className="StickyContainer">
        <Prompt when={true} message={() => !isSessionExpired(lastActiveTime)}></Prompt>
        <div className="PageWrapper PageWrapper-noNav">
          <section className="Login-wrapper " style={styleBg}>
            {!isPackageSold ? (
              <>
                <div className="Card">
                  <div>
                    {renderSearchPanel && <SearchPanel></SearchPanel>}
                    {error}
                    {renderSearchPanel ? (
                      <ParseHtml
                        htmlInput={localize(
                          'preview-sales-engine.static-content.copy.associate-search-page.search-page-eligibility',
                        ).replace('classname', 'class')}
                      />
                    ) : (
                      ''
                    )}{' '}
                    {spinner}
                    {prospect}
                    {locDescription}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="Card">
                  <SoldPackageInformationDetails
                    packageInformation={soldPackageInformation}
                  ></SoldPackageInformationDetails>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
};
export default withRouter(withLocalization(withRoles(OfferSearch)));
